// The web app's call manager: CallManager (callManagerCore.ts) over the data WORKER. The worker
// owns REST and the main socket (data/features/calls.ts): it hands the manager the active-call table
// as a `calls` signal (the ring decision and the banners read it), the plugin config at join, and
// the decline. The call socket authenticates with the page's own session cookie (same origin).
//
// The Windows app's hidden call host (src/callhost/) runs the same CallManager over a different
// backend — the native app — which is why the class lives in a file that imports no worker.
import { data } from '../../../bridge/client';
import { buildCallFailureReport, ERROR_REPORT_PATH } from '../../../core/errorReports';
import { mediaUrls } from '../../media';
import { voicePlayback } from '../voice/playback';
import { CallManager } from './callManagerCore';
import { wireCallStatus } from './callStatus';

export * from './callManagerCore';

/** At most 3 call-failure reports per page load. */
let callFailureReports = 0;

export const callManager = new CallManager({
  onActiveCalls(listener) {
    data.onSignal((s) => {
      if (s.kind === 'calls') listener(s.view);
    });
  },
  callsState: () => data.call('callsState'),
  refreshCalls: () => data.call('refreshCalls'),
  callsConfig: () => data.call('callsConfig'),
  dismissCallNotification: (channelId) => data.call('dismissCallNotification', channelId),
  callPeople: (userIds) => data.call('callPeople', userIds),
  token: () => null,
  interruptMedia() {
    // A call takes the microphone and the speaker: a voice note being recorded PAUSES (the voice
    // feature listens for this event — its recorder.ts), one being played stops.
    window.dispatchEvent(new Event('satori:media-interrupt'));
    voicePlayback.stop();
  },
  avatarUrl: (userId) => mediaUrls.avatar(userId, 0),
  reportCallFailure(description, log) {
    // Same door as the page's error reports (ui/errorReporting.ts): signed out = nothing to send.
    const userId = /(?:^|; )MMUSERID=([^;]*)/.exec(document.cookie)?.[1] ?? null;
    if (!import.meta.env.PROD || !userId || ++callFailureReports > 3) return;
    const body = buildCallFailureReport({
      description, log, at: Date.now(), version: __APP_VERSION__, build: __APP_BUILD__, userId: decodeURIComponent(userId), userAgent: navigator.userAgent,
    });
    void fetch(ERROR_REPORT_PATH, {
      method: 'POST', credentials: 'same-origin', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).catch(() => undefined);
  },
});

// «En llamada» (shared/API.md §38) follows this manager's phase.
wireCallStatus(callManager);
